/**
 * Le rangement des fichiers — trois appels, écrits à la main.
 *
 * POURQUOI AUCUNE BIBLIOTHÈQUE. L'interface n'emploie que `@supabase/auth-js`,
 * jamais le paquet complet `supabase-js`, et c'est délibéré : privée de client
 * de base de données, elle est dans l'INCAPACITÉ MATÉRIELLE de contourner
 * l'API (cadrage v2, §4). Ajouter `supabase-js` pour envoyer un fichier
 * rouvrirait cette porte pour rien.
 *
 * Le service de rangement, lui, n'est pas la base : c'est un service HTTP à
 * part, qui ne sait rien lire des tables. Trois appels suffisent, et les écrire
 * ici les rend lisibles — on voit exactement ce qui part et avec quel jeton.
 *
 * CE QUI AUTORISE, ET CE QUI N'AUTORISE PAS. Le jeton est celui de la personne
 * connectée. La clé publique en `apikey` n'ouvre rien à elle seule : ce sont
 * les règles posées sur `storage.objects` (migration `20260904190000`) qui
 * tranchent, en comparant la société inscrite dans le chemin à celle du compte.
 * Une adresse devinée ne donne donc rien.
 */

import { configuration } from './configuration.js';

/** L'espace de rangement des pièces jointes. Un seul, privé. */
const ESPACE = 'documents';

const BASE = `${configuration.supabaseUrl}/storage/v1`;

export type EchecRangement = 'refus' | 'trop_gros' | 'injoignable';

export type ResultatRangement<T> =
  | { readonly etat: 'ok'; readonly valeur: T }
  | { readonly etat: 'echec'; readonly cause: EchecRangement };

function entetes(jeton: string): Record<string, string> {
  return {
    Authorization: `Bearer ${jeton}`,
    apikey: configuration.supabaseClePublique,
  };
}

function echecDe(statut: number): EchecRangement {
  if (statut === 413) return 'trop_gros';
  if (statut === 401 || statut === 403 || statut === 400) return 'refus';
  return 'injoignable';
}

/**
 * Envoie le fichier au chemin décidé par l'API.
 *
 * Le chemin n'est JAMAIS composé ici : il vient de la réponse à la déclaration
 * du document. C'est ce qui garantit qu'il commence par la bonne société — la
 * même garantie que le `check` de la table et que les règles de rangement.
 */
export async function envoyerLeFichier(
  jeton: string,
  chemin: string,
  fichier: File,
): Promise<ResultatRangement<null>> {
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}/object/${ESPACE}/${encodeURI(chemin)}`, {
      method: 'POST',
      headers: {
        ...entetes(jeton),
        // Le type déclaré par le navigateur, ou rien : deviner le type d'un
        // fichier d'après son nom serait se tromper un jour sur deux.
        ...(fichier.type === '' ? {} : { 'content-type': fichier.type }),
        // Pas d'écrasement : chaque pièce a son propre identifiant dans son
        // nom, et une collision signifierait qu'on écrase ce qu'on ne voit pas.
        'x-upsert': 'false',
      },
      body: fichier,
    });
  } catch {
    return { etat: 'echec', cause: 'injoignable' };
  }

  if (!reponse.ok) {
    return { etat: 'echec', cause: echecDe(reponse.status) };
  }
  return { etat: 'ok', valeur: null };
}

/**
 * Une adresse signée, valable quelques minutes, pour ouvrir la pièce.
 *
 * Jamais d'adresse publique : l'espace est privé, et un lien qui n'expire pas
 * finirait par circuler par courriel.
 */
export async function adresseDuFichier(
  jeton: string,
  chemin: string,
  secondes = 300,
): Promise<ResultatRangement<string>> {
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}/object/sign/${ESPACE}/${encodeURI(chemin)}`, {
      method: 'POST',
      headers: { ...entetes(jeton), 'content-type': 'application/json' },
      body: JSON.stringify({ expiresIn: secondes }),
    });
  } catch {
    return { etat: 'echec', cause: 'injoignable' };
  }

  if (!reponse.ok) {
    return { etat: 'echec', cause: echecDe(reponse.status) };
  }

  const corps = (await reponse.json()) as { signedURL?: string };
  if (corps.signedURL === undefined) {
    return { etat: 'echec', cause: 'injoignable' };
  }
  return { etat: 'ok', valeur: `${BASE}${corps.signedURL}` };
}

/**
 * Efface le fichier.
 *
 * Appelé AVANT la suppression de la ligne : si l'effacement échoue, rien n'a
 * bougé et l'écran le dit. Dans l'autre ordre, un fichier resterait sans rien
 * pour le désigner — invisible, et payant.
 */
export async function effacerLeFichier(
  jeton: string,
  chemin: string,
): Promise<ResultatRangement<null>> {
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}/object/${ESPACE}/${encodeURI(chemin)}`, {
      method: 'DELETE',
      headers: entetes(jeton),
    });
  } catch {
    return { etat: 'echec', cause: 'injoignable' };
  }

  if (reponse.ok) return { etat: 'ok', valeur: null };

  // 404 : le fichier n'est pas là — une pièce restée en « en_envoi », par
  // exemple. La ligne doit pouvoir s'effacer quand même.
  if (reponse.status === 404) return { etat: 'ok', valeur: null };

  /**
   * 400 « InvalidKey » : LE RANGEMENT REFUSE CE NOM, donc aucun fichier n'a
   * jamais pu y être écrit. Il n'y a rien à effacer, et la ligne doit partir.
   *
   * Constaté le 08/09/2026 sur la base de TNS. Les pièces déclarées AVANT le
   * correctif de `cleDeRangement` portent un chemin accentué : leur dépôt
   * échouait par un 400, et leur suppression recevait le MÊME 400. Comme seul
   * le 404 était toléré, ces lignes étaient **indestructibles depuis l'écran**
   * — trois d'entre elles ont dû être retirées par un appel direct à l'API.
   *
   * Sans cette tolérance, le correctif ne réparerait que les dépôts à venir et
   * laisserait les lignes déjà écrites bloquées pour toujours.
   */
  if (reponse.status === 400) {
    // Le corps peut ne pas se lire — on ne veut pas d'une panne de lecture à
    // la place d'un refus. Dans ce cas la chaîne reste vide et le 400 redevient
    // un échec ordinaire.
    let corps: string;
    try {
      corps = await reponse.text();
    } catch {
      corps = '';
    }
    if (corps.includes('InvalidKey')) return { etat: 'ok', valeur: null };
  }

  return { etat: 'echec', cause: echecDe(reponse.status) };
}

/** « 861234 » → « 841 ko ». Rend `null` quand le poids est inconnu. */
export function poidsDit(poids: string | number | null): string | null {
  if (poids === null) return null;
  const octets = typeof poids === 'string' ? Number(poids) : poids;
  if (!Number.isFinite(octets) || octets < 0) return null;
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  const mega = octets / (1024 * 1024);
  return `${mega >= 10 ? Math.round(mega) : Math.round(mega * 10) / 10} Mo`.replace(
    '.',
    ',',
  );
}

/** Le poids au-delà duquel l'écran refuse avant même d'essayer. */
export const POIDS_MAXIMUM = 20 * 1024 * 1024;
